/**
 * Balanced assignment + portfolio computation.
 *
 * This is a faithful port of the prototype's `balancedAssign` / `assignTier` /
 * `computePortfolios` (reference_implementation.html lines 597-670), pinned by
 * tests/parity against a golden file generated by executing those very
 * functions. Two behaviours are deliberately preserved because they are real and
 * load-bearing, and one is deliberately fixed.
 *
 * PRESERVED — balancing is by account COUNT, not spend. Per-CSM booking volume
 * therefore comes out uneven even when counts match: in the reference snapshot
 * one enterprise CSM carries 2.7x the booking volume of each of the other two,
 * on a portfolio one account larger. That is the algorithm working as designed.
 * A spend-balancing mode is a planned option, not a correction.
 *
 * PRESERVED — pinning an account to a CSM REDUCES their distributed share, by
 * spending a unit of their capacity. Counter-intuitive, and load-bearing.
 *
 * FIXED — accounts pinned to someone who holds no slot no longer vanish. See
 * computePortfolios().
 */

import type {
  Account,
  AssignmentCoverage,
  BalanceBy,
  Pins,
  Portfolio,
  StaffingConfig,
  StaffingResult,
  TierPins,
} from './types.ts';
import { SENTINEL_CSMS } from './types.ts';
import { bucketByTier, isNamedTier } from './tiering.ts';

/**
 * Reject a CSM appearing twice in the SAME tier's slots.
 *
 * Throws rather than de-duplicating, because the two readings of a repeated slot
 * are irreconcilable and only the caller knows which was meant: "this person
 * counts twice toward capacity" (which is what `target = ceil(n / slots.length)`
 * would do) or "this is a typo". Silently collapsing it picks one and hides the
 * question.
 *
 * Left unchecked it corrupted totals invisibly: `balancedAssign` keys its
 * buckets by CSM so the duplicate collapsed there, but `for (const csm of slots)`
 * then emitted TWO portfolios holding the same `assigned` array, and
 * `computeCoverage` does not treat same-CSM repeats as double-assignment — so
 * coverage stayed green while `portfolioLoad` summed the book twice.
 *
 * The same CSM in DIFFERENT tiers is legitimate and deliberate (D3).
 */
export function assertSlotsUnique(config: StaffingConfig): void {
  for (const tier of config.tiers) {
    const slots = config.slots[tier.key] ?? [];
    const seen = new Set<string>();
    for (const csm of slots) {
      if (seen.has(csm)) {
        throw new Error(
          `staffing: '${csm}' appears more than once in tier '${tier.key}' slots — ` +
            `a CSM may staff several tiers, but may not occupy two slots in one`,
        );
      }
      seen.add(csm);
    }
  }
}

/**
 * Validate the inputs the engine cannot recover from.
 *
 * Every check here exists because the failure it prevents is SILENT — the engine
 * produces a confident, wrong answer rather than an error:
 *
 *  - an unknown pin target minted a portfolio for whatever string it was given,
 *    so a stale or malformed pin quietly became a customer's owner
 *  - duplicate account ids collapse in the coverage map, so two rows can land on
 *    different CSMs while coverage still reports "everything is assigned"
 *  - NaN spend compares false against every threshold and falls to the lowest
 *    tier; a NaN threshold passes the ordering check; negative hours yield
 *    negative FTE demand
 */
export function assertInputsValid(
  accounts: readonly Account[],
  config: StaffingConfig,
  pins: Pins,
): void {
  // An unrecognised balanceBy is refused rather than defaulted.
  //
  // `undefined` is a real answer meaning "the caller did not ask", and resolves
  // to 'count'. Any OTHER value is a caller who asked for something specific and
  // would otherwise receive count-balancing with no error anywhere — a typo of
  // 'spend' silently producing the exact wrong book. That is the same failure
  // this mode was added to fix, so defaulting past it here would reintroduce it
  // one layer up. TypeScript stops typed callers; the stored config arrives as
  // JSON and is cast, so it does not stop this one.
  if (
    config.balanceBy !== undefined &&
    config.balanceBy !== 'count' &&
    config.balanceBy !== 'spend'
  ) {
    throw new Error(
      `staffing: unrecognised balanceBy '${String(config.balanceBy)}' — ` +
        `expected 'count' or 'spend'; defaulting would silently count-balance a book someone asked to spend-balance`,
    );
  }

  const seenIds = new Set<string>();
  for (const account of accounts) {
    if (seenIds.has(account.id)) {
      throw new Error(
        `staffing: duplicate account id '${account.id}' — coverage cannot tell the two apart`,
      );
    }
    seenIds.add(account.id);
    if (!Number.isFinite(account.spendM)) {
      throw new Error(
        `staffing: account '${account.id}' has non-finite spendM (${account.spendM}) — ` +
          `it would silently fall into the lowest tier`,
      );
    }
  }

  for (const tier of config.tiers) {
    // Both infinities are legitimate, and they mean opposite things:
    //   -Infinity on the catch-all BAND — takes everything below the last floor.
    //   +Infinity on a NAMED tier — takes nothing by spend, only by placement.
    // Which one is allowed depends on the tier's kind, so a named tier carrying
    // -Infinity (and thus quietly competing with the catch-all) is still refused.
    const permitted = isNamedTier(tier)
      ? Number.POSITIVE_INFINITY
      : Number.NEGATIVE_INFINITY;
    if (
      Number.isNaN(tier.minSpendM) ||
      (!Number.isFinite(tier.minSpendM) && tier.minSpendM !== permitted)
    ) {
      throw new Error(
        `staffing: tier '${tier.key}' has an unusable minSpendM (${tier.minSpendM})`,
      );
    }
    if (
      !Number.isFinite(tier.hoursPerAccountPerMonth) ||
      tier.hoursPerAccountPerMonth < 0
    ) {
      throw new Error(
        `staffing: tier '${tier.key}' has invalid hoursPerAccountPerMonth (${tier.hoursPerAccountPerMonth})`,
      );
    }
  }
  if (!Number.isFinite(config.hrsPerFteYear) || config.hrsPerFteYear <= 0) {
    throw new Error(
      `staffing: hrsPerFteYear must be a positive, finite number (got ${config.hrsPerFteYear})`,
    );
  }

  const roster = new Set(config.roster);
  if (roster.size === 0) {
    throw new Error(
      'staffing: config.roster is empty — no CSM may be assigned work',
    );
  }
  for (const tier of config.tiers) {
    for (const csm of config.slots[tier.key] ?? []) {
      if (!roster.has(csm)) {
        throw new Error(
          `staffing: tier '${tier.key}' has a slot for '${csm}', who is not in the roster`,
        );
      }
    }
  }
  for (const [accountId, csm] of Object.entries(pins)) {
    if (!roster.has(csm)) {
      throw new Error(
        `staffing: account '${accountId}' is pinned to '${csm}', who is not in the roster — ` +
          `refusing to invent an owner`,
      );
    }
    // A pin whose KEY is not a real account is the same silent failure from the
    // other end: it survives validation, is ignored by assignment because
    // nothing matches it, and never appears in coverage — so persistence can
    // store a no-op pin and the UI can show a rule that does nothing, with no
    // error anywhere. A deleted or re-keyed customer produces exactly this.
    if (!seenIds.has(accountId)) {
      throw new Error(
        `staffing: pin references account '${accountId}', which is not in the book — ` +
          `a stale pin would be silently ignored`,
      );
    }
  }
}

/** Is this account's current owner a real person who staffs this tier? */
function isLockedTo(account: Account, slotSet: ReadonlySet<string>): boolean {
  const cur = account.currentCsm;
  return cur !== null && !SENTINEL_CSMS.has(cur) && slotSet.has(cur);
}

/**
 * Distribute one tier's unpinned accounts across its slots.
 *
 * TWO MODES, one traversal. Both walk the accounts in the same order — spend
 * DESCENDING, ties by id — and differ only in which slot they call emptiest.
 *
 * `'count'` is faithful to the reference:
 *   target   = ceil(ALL accounts in tier / slots)   ← includes pinned accounts
 *   capacity = max(0, target − pinnedInThisTier − alreadyLocked)
 *   choice   = slot with the most remaining capacity; ties go to the FIRST slot
 *
 * `'spend'` equalizes BOOKING VOLUME instead:
 *   load     = spend already committed to that slot (locked + pinned in tier)
 *   choice   = slot with the least load; ties go to the FIRST slot
 *
 * The descending order is what makes the spend mode work rather than merely run.
 * Greedy bin-packing that places the largest item first (longest-processing-time
 * first) is the standard approach for exactly this problem; feeding it ascending
 * would let a single $40M customer arrive last and land on whichever book was
 * marginally lightest, undoing the balance the previous hundred placements built.
 * The order was already here for the count mode's benefit, so the spend mode
 * inherits the property for free — but it is now load-bearing rather than
 * incidental, which is why it is stated here as well as at the sort.
 *
 * Returns a map of slot name → accounts distributed to it.
 */
function balancedAssign(
  tierAccounts: readonly Account[],
  slots: readonly string[],
  pins: Pins,
  balanceBy: BalanceBy = 'count',
): Map<string, Account[]> {
  const slotSet = new Set(slots);
  const buckets = new Map<string, Account[]>();
  const capacity = new Map<string, number>();
  for (const slot of slots) buckets.set(slot, []);

  // ONE SLOT: there is nothing to balance, so nothing is balanced.
  //
  // The general path below reaches the same ANSWER — with a single slot every
  // account lands on that one person regardless of mode — but it gets there by
  // sorting the tier and running a capacity search per account, which is both
  // wasted work at 593 accounts and a lie about what happened. "Balanced by
  // spend across one CSM" is not a decision anybody made.
  //
  // This is the real shape of the Small tier as of the 2026-08 restructure: one
  // CSM, the entire band. Lopsided, and it is what the team has. The engine's job
  // is to report that honestly, not to dress it up as a distribution — the
  // capacity and FTE figures downstream are what make the imbalance visible, and
  // they are unaffected by this.
  //
  // Membership is IDENTICAL to the general path (pinned accounts are handled by
  // the caller in both), and input order is preserved rather than re-sorted, so
  // the result stays deterministic for a caller that fixed its read order.
  if (slots.length === 1) {
    const only = slots[0]!;
    buckets.set(
      only,
      tierAccounts.filter((account) => pins[account.id] === undefined),
    );
    return buckets;
  }

  const toDistribute: Account[] = [];
  const lockedBySlot = new Map<string, Account[]>();
  for (const slot of slots) lockedBySlot.set(slot, []);
  for (const account of tierAccounts) {
    if (pins[account.id] !== undefined) continue; // pinned — handled separately
    if (isLockedTo(account, slotSet))
      lockedBySlot.get(account.currentCsm!)!.push(account);
    else toDistribute.push(account);
  }

  const target = Math.ceil(tierAccounts.length / slots.length);
  const fairSpend =
    tierAccounts.reduce((sum, a) => sum + a.spendM, 0) / slots.length;

  // THE LOCK YIELDS TO CAPACITY. A slot keeps at most its FAIR SHARE of the
  // accounts already locked to it; the rest are released to the distributor.
  //
  // WHY THIS EXISTS. `isLockedTo` used to be absolute: an account stayed with
  // its current CSM whenever that CSM staffed the tier, no matter how many they
  // already held. That is right for churn — nobody wants a relationship moved to
  // even out a spreadsheet — and it made the tool unable to answer its main
  // question. Moving a CSM into a tier, or modelling a hire there, found every
  // account already locked to the incumbents, so the newcomer received NOTHING
  // and the existing books did not move. "Add a person and watch the load
  // redistribute" is the whole point of a what-if, and the engine's answer was
  // always "no change".
  //
  // The cap is the smallest rule that restores it: incumbents keep their share,
  // and only the surplus moves. With N accounts over S slots nobody keeps more
  // than ceil(N/S), so adding a slot necessarily frees work for it.
  //
  // LARGEST KEPT, SMALLEST RELEASED. Ordering by spend descending and releasing
  // from the tail keeps the biggest relationships with the person who has them —
  // a $40M customer is not the one to hand over to balance headcount — and makes
  // the release deterministic rather than dependent on read order.
  //
  // Parity is unaffected: in the reference book no slot holds more than its fair
  // share, so nothing is released and the goldens reproduce exactly. Verified
  // before this was written, not assumed.
  // PINS DO NOT CONSUME THE ALLOWANCE, and that is a deliberate asymmetry.
  //
  // The distribution capacity below DOES subtract pins — a slot already holding
  // pinned work should receive fewer NEW accounts. But charging pins against the
  // keep-allowance means pinning a customer TO someone can push a different
  // customer AWAY from them, which is the opposite of what a pin says. A pin is
  // an explicit human decision to keep one account with one person; it must not
  // cause collateral movement of another. (Caught by
  // `losing reports an account pinned AWAY to another CSM`, whose second half
  // asserts that a self-pin loses nothing.)
  for (const slot of slots) {
    const ordered = [...lockedBySlot.get(slot)!].sort(
      (a, b) => b.spendM - a.spendM || a.id.localeCompare(b.id),
    );
    const kept: Account[] = [];

    if (balanceBy === 'spend') {
      // Budgeted by BOOKING VOLUME, since that is what this mode equalizes.
      let carried = 0;
      for (const account of ordered) {
        // The largest is always kept: a single account bigger than the fair
        // share cannot be split, and releasing it only moves the imbalance to
        // whoever receives it.
        if (kept.length > 0 && carried + account.spendM > fairSpend) {
          toDistribute.push(account);
          continue;
        }
        kept.push(account);
        carried += account.spendM;
      }
    } else {
      for (const account of ordered) {
        if (kept.length >= target) {
          toDistribute.push(account);
          continue;
        }
        kept.push(account);
      }
    }
    buckets.set(slot, kept);
  }

  for (const slot of slots) {
    const pinnedInTier = tierAccounts.filter((a) => pins[a.id] === slot).length;
    capacity.set(
      slot,
      Math.max(0, target - pinnedInTier - buckets.get(slot)!.length),
    );
  }

  // Spend already committed to each slot, for the 'spend' mode. Locked and
  // pinned accounts count: they are that person's work whether or not the
  // distributor chose them, so starting every slot at zero would hand the
  // heaviest existing book yet more of the largest remaining customers.
  //
  // NEGATIVE SPEND LOWERS A LOAD, deliberately. Credits and adjustments are
  // ordinary here (the reference snapshot has 19 accounts at or below zero), and
  // netting them is what makes a load mean "booking volume this person carries".
  // They sort last, so they land after the real balance is established and move
  // it by fractions of a million at most.
  const load = new Map<string, number>();
  for (const slot of slots) {
    const lockedSpend = buckets
      .get(slot)!
      .reduce((sum, a) => sum + a.spendM, 0);
    const pinnedSpend = tierAccounts
      .filter((a) => pins[a.id] === slot)
      .reduce((sum, a) => sum + a.spendM, 0);
    load.set(slot, lockedSpend + pinnedSpend);
  }

  // Spend DESCENDING, ties broken by id: the largest accounts are placed while
  // capacity is widest, and equal-spend accounts are placed in a fixed order.
  //
  // The tiebreak is not cosmetic. Array.prototype.sort is stable, and the slot
  // search below keeps the FIRST slot at maximum capacity, so tied rows are
  // resolved purely by input position — which for a database read is whatever
  // order the rows arrived in. Zero-spend and rounded values make ties ordinary,
  // not rare, so without this the same book could distribute two different ways
  // on two reads and a saved scenario would not reproduce. The route pins the
  // read order too; this pins the engine's half.
  //
  // Sort a copy — mutating the caller's array would make the engine order-dependent.
  const ordered = [...toDistribute].sort(
    (a, b) => b.spendM - a.spendM || a.id.localeCompare(b.id),
  );

  for (const account of ordered) {
    // Both comparisons are STRICT, so the FIRST slot holding the best value wins
    // a tie. That is the reference's rule for counts, and keeping it for spend
    // means an all-zero-spend tier distributes in slot order rather than by
    // whatever order the rows arrived in — the same determinism the id tiebreak
    // above protects.
    let best = slots[0]!;
    if (balanceBy === 'spend') {
      let bestLoad = load.get(best)!;
      for (const slot of slots) {
        const l = load.get(slot)!;
        if (l < bestLoad) {
          best = slot;
          bestLoad = l;
        }
      }
    } else {
      let bestCapacity = capacity.get(best)!;
      for (const slot of slots) {
        const cap = capacity.get(slot)!;
        if (cap > bestCapacity) {
          best = slot;
          bestCapacity = cap;
        }
      }
    }
    buckets.get(best)!.push(account);
    // Both metrics are kept current regardless of mode. They are cheap, and a
    // half-maintained counter is how a later change to this function picks the
    // wrong slot while every test still passes.
    capacity.set(best, Math.max(0, capacity.get(best)! - 1));
    load.set(best, load.get(best)! + account.spendM);
  }

  return buckets;
}

/**
 * Compute every CSM's book.
 *
 * THE FIX. The reference wrote a portfolio only for names holding a tier slot,
 * while removing every pinned account from distribution — so an account pinned
 * to someone with no slot was taken out of the pool and then placed nowhere. In
 * the shipped snapshot that silently dropped 23 of 704 accounts (all pinned to
 * the manager, who staffs no tier), and the prototype hid it with a special-case
 * renderer. Every total, export and FTE figure it produced was therefore
 * computed over 681 accounts while the screen showed 704.
 *
 * Here, a pin target with no slot gets a real portfolio with `tier: null`, and
 * `coverage` is returned so a caller can assert totality rather than trust it.
 */
export function computePortfolios(
  accounts: readonly Account[],
  config: StaffingConfig,
  pins: Pins = {},
  tierPins: TierPins = {},
): StaffingResult {
  // `tierPins` is a SEPARATE parameter from `pins` because they answer separate
  // questions — which band of service this account gets, and who holds it. An
  // account can need either, both, or neither, and folding them together would
  // make "move this strategic account to Dev" impossible to say without also
  // restating its tier.
  const byTier = bucketByTier(accounts, config.tiers, tierPins);

  assertSlotsUnique(config);
  assertInputsValid(accounts, config, pins);

  const portfolios: Portfolio[] = [];
  const hasPortfolio = new Set<string>();
  // A CSM's pinned book is attached to exactly one of their portfolios; this
  // records who has already received theirs. See Portfolio.pinned.
  const pinsAttached = new Set<string>();
  const unallocated: Record<string, Account[]> = {};
  /** Which tiers each CSM actually staffs — `otherOut` must not claim these. */
  const staffedTiers = new Map<string, Set<string>>();
  /** The one portfolio per CSM that carries their cross-tier facts. */
  const primaryPortfolio = new Map<string, Portfolio>();

  for (const tier of config.tiers) {
    const slots = config.slots[tier.key] ?? [];
    if (slots.length === 0) {
      // An unstaffed tier. Surfaced explicitly rather than skipped — its book
      // would otherwise disappear from every portfolio and every total.
      //
      // PINNED accounts are excluded: a pin is tier-independent, so an account
      // pinned to someone is OWNED by that person's portfolio even when the tier
      // it sits in has no slots. Including them here made the same account both
      // unallocated and owned, so `unallocated` and `coverage` contradicted each
      // other — one saying nobody has it, the other naming who does.
      const orphaned = byTier[tier.key]!.filter(
        (a) => pins[a.id] === undefined,
      );
      if (orphaned.length > 0) unallocated[tier.key] = orphaned;
      continue;
    }

    const tierAccounts = byTier[tier.key]!;
    const distributed = balancedAssign(
      tierAccounts,
      slots,
      pins,
      config.balanceBy ?? 'count',
    );

    for (const csm of slots) {
      const assigned = distributed.get(csm) ?? [];
      const assignedIds = new Set(assigned.map((a) => a.id));

      // The CSM's CURRENT book at this tier, derived from the accounts
      // themselves. The reference carried a separate CURRENT map; it is exactly
      // reproducible from `currentCsm` (verified across all 704), and a second
      // denormalized copy is a second thing that can disagree.
      const currentHere = tierAccounts.filter((a) => a.currentCsm === csm);
      const currentHereIds = new Set(currentHere.map((a) => a.id));

      // Highest tier this CSM staffs wins their pinned book; tiers are iterated
      // in config order, which is descending by threshold.
      const takesPins = !pinsAttached.has(csm);
      if (takesPins) pinsAttached.add(csm);

      portfolios.push({
        csm,
        tier: tier.key,
        assigned,
        pinned: takesPins ? accounts.filter((a) => pins[a.id] === csm) : [],
        keeping: assigned.filter(
          (a) => currentHereIds.has(a.id) && a.currentCsm !== 'New',
        ),
        incoming: assigned.filter(
          (a) => !currentHereIds.has(a.id) || a.currentCsm === 'New',
        ),
        // DEVIATION (documented): the reference excluded EVERY pinned account
        // from `losing` (`!overrides[a.name]` — a truthiness check on the whole
        // pin map). That means a customer owned by A and pinned to B vanished
        // from A's loss list, so the change list a manager works from omitted a
        // real reassignment. Only a pin to THIS CSM means "retained".
        losing: currentHere.filter(
          (a) => !assignedIds.has(a.id) && pins[a.id] !== csm,
        ),
        otherOut: {},
      });
      hasPortfolio.add(csm);
      const alreadyStaffed = staffedTiers.get(csm);
      if (alreadyStaffed) alreadyStaffed.add(tier.key);
      else staffedTiers.set(csm, new Set([tier.key]));
      if (takesPins)
        primaryPortfolio.set(csm, portfolios[portfolios.length - 1]!);
    }
  }

  // Pin targets holding no slot — the manager, and anyone else pinned to
  // without being on a team. Without this they would own accounts that exist in
  // no portfolio, which is the bug described above.
  const offSlotTargets = [...new Set(Object.values(pins))]
    .filter((csm) => !hasPortfolio.has(csm))
    .sort();
  for (const csm of offSlotTargets) {
    portfolios.push({
      csm,
      tier: null,
      assigned: [],
      pinned: accounts.filter((a) => pins[a.id] === csm),
      keeping: [],
      incoming: [],
      losing: [],
      otherOut: {},
    });
    primaryPortfolio.set(csm, portfolios[portfolios.length - 1]!);
  }

  // DEVIATION 3 (documented): current owners who staff no tier and hold no pins.
  //
  // The reference wrote portfolios only for slot holders, so a CSM who owns
  // accounts today but is on no team tomorrow simply disappeared: their book was
  // redistributed and NOTHING recorded the departure. `coverage` stays green —
  // every account has a new owner — which is exactly why this hid. The receiving
  // CSMs show `incoming`; no one shows the matching outgoing, so the change list
  // a manager works from does not balance.
  //
  // That is not an edge case here, it is the primary scenario: "what if we move
  // this person off the team" is the question the tool exists to answer.
  //
  // Sentinels are excluded — 'New' and 'Client Services' are placeholders for
  // "nobody owns this", not people who can lose a book.
  const departingOwners = [
    ...new Set(accounts.map((a) => a.currentCsm)),
  ].filter(
    (csm): csm is string =>
      csm !== null && !SENTINEL_CSMS.has(csm) && !primaryPortfolio.has(csm),
  );
  departingOwners.sort();
  for (const csm of departingOwners) {
    portfolios.push({
      csm,
      tier: null,
      assigned: [],
      pinned: [],
      keeping: [],
      incoming: [],
      losing: [],
      otherOut: {},
    });
    primaryPortfolio.set(csm, portfolios[portfolios.length - 1]!);
  }

  // ── otherOut, computed once the whole assignment is known ─────────────────
  //
  // "Accounts I hold today, in a tier I do NOT staff, that I will not keep."
  //
  // Computed here rather than inside the tier loop because it needs the RESOLVED
  // assignment. The first version listed a CSM's accounts in every other tier as
  // leaving — including tiers they also staff, where those accounts are retained
  // — and emitted the same map on each of their portfolios, so a multi-tier CSM
  // showed inflated, duplicated transfer counts. Tiers a CSM does staff report
  // their own departures through `losing`; this covers only the rest.
  const retained = new Map<string, Set<string>>();
  for (const portfolio of portfolios) {
    let set = retained.get(portfolio.csm);
    if (!set) {
      set = new Set<string>();
      retained.set(portfolio.csm, set);
    }
    for (const a of [...portfolio.assigned, ...portfolio.pinned]) set.add(a.id);
  }
  for (const [csm, portfolio] of primaryPortfolio) {
    const staffed = staffedTiers.get(csm) ?? new Set<string>();
    const keep = retained.get(csm) ?? new Set<string>();
    for (const tier of config.tiers) {
      if (staffed.has(tier.key)) continue;
      const leaving = byTier[tier.key]!.filter(
        (a) => a.currentCsm === csm && !keep.has(a.id),
      );
      if (leaving.length > 0) portfolio.otherOut[tier.key] = leaving;
    }
  }

  return {
    byTier,
    portfolios,
    coverage: computeCoverage(accounts, portfolios),
    unallocated,
  };
}

/**
 * Who owns what, and does it add up.
 *
 * Returned rather than asserted so the caller decides what a gap means — but
 * both lists being empty is the invariant the parity suite pins.
 */
export function computeCoverage(
  accounts: readonly Account[],
  portfolios: readonly Portfolio[],
): AssignmentCoverage {
  const owner = new Map<string, string>();
  const doubleAssigned: string[] = [];

  for (const portfolio of portfolios) {
    const seen = new Set<string>();
    for (const account of [...portfolio.assigned, ...portfolio.pinned]) {
      if (seen.has(account.id)) continue; // a pin can also be distributed to the same CSM
      seen.add(account.id);
      const existing = owner.get(account.id);
      if (existing !== undefined && existing !== portfolio.csm) {
        doubleAssigned.push(account.id);
      } else {
        owner.set(account.id, portfolio.csm);
      }
    }
  }

  return {
    total: accounts.length,
    assigned: owner.size,
    unassigned: accounts.filter((a) => !owner.has(a.id)).map((a) => a.id),
    doubleAssigned,
  };
}

/** Every distinct account in a portfolio — pins and distributed, deduplicated. */
export function portfolioAccounts(portfolio: Portfolio): Account[] {
  const seen = new Set<string>();
  return [...portfolio.assigned, ...portfolio.pinned].filter((a) =>
    seen.has(a.id) ? false : (seen.add(a.id), true),
  );
}
